"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MessageCircle, X, ArrowLeft } from "lucide-react";
import ChatPanel from "@/components/chat-panel";
import ThreadList, { THREAD_LABELS } from "@/components/chat/thread-list";
import type { ChatUser, ThreadType } from "@/types/chat";

interface FloatingChatProps {
  currentUser: { id: string; username: string } | null;
  users: ChatUser[];
}

/** Desktop floating chat: FAB that opens a thread-list/chat panel. */
export default function FloatingChat({ currentUser, users }: FloatingChatProps) {
  const [open, setOpen] = useState(false);
  const [activeThread, setActiveThread] = useState<{
    type: ThreadType;
    id: string;
  } | null>(null);

  if (!currentUser) return null;

  return (
    <>
      {/* FAB */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed right-6 bottom-6 z-50 hidden size-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-all hover:bg-primary/90 md:flex"
          aria-label="Открыть чаты"
        >
          <MessageCircle className="size-5" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed right-6 bottom-6 z-50 hidden h-[520px] w-[380px] flex-col overflow-hidden rounded-xl border bg-background shadow-2xl md:flex">
          {/* Header */}
          <div className="flex items-center gap-2 border-b bg-muted/30 px-4 py-3">
            {activeThread && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setActiveThread(null)}
                aria-label="Назад к списку чатов"
              >
                <ArrowLeft className="size-4" />
              </Button>
            )}
            <h3 className="flex-1 text-sm font-semibold">
              {activeThread
                ? (THREAD_LABELS[activeThread.type] ?? activeThread.type)
                : "Чаты"}
            </h3>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => {
                setOpen(false);
                setActiveThread(null);
              }}
              aria-label="Закрыть чаты"
            >
              <X className="size-4" />
            </Button>
          </div>

          {/* Content */}
          {activeThread ? (
            <ChatPanel
              threadType={activeThread.type}
              threadId={activeThread.id}
              currentUser={currentUser}
              users={users}
              className="flex-1 rounded-none border-0"
              hideHeader
            />
          ) : (
            <ThreadList active={open && !activeThread} onSelect={setActiveThread} />
          )}
        </div>
      )}
    </>
  );
}
